import {
  ASSEMBLYAI_RECOVERY_DB_NAME,
  LEGACY_TRANSCRIPT_SNAPSHOT_KEY,
  SCRIBE_RECOVERY_DB_NAME,
} from "../constants/live-transcription";

const CLEAR_TIMEOUT_MS = 1000;

type Disowner = () => Promise<void>;
const disowners = new Set<Disowner>();

/** The recovery store registers here so logout can stop a live recording's writes before the delete. */
export const registerRecoveryDisowner = (disown: Disowner): (() => void) => {
  disowners.add(disown);
  return () => disowners.delete(disown);
};

type SavedRecordingCheck = () => Promise<boolean>;
const savedRecordingChecks = new Set<SavedRecordingCheck>();

/**
 * Each recovery store registers here so logout can ask, before deleting
 * everything, whether it still holds a recording (MDRS-551).
 */
export const registerSavedRecordingCheck = (
  check: SavedRecordingCheck
): (() => void) => {
  savedRecordingChecks.add(check);
  return () => savedRecordingChecks.delete(check);
};

/**
 * Whether this browser still holds an unsent or interrupted recording for ANY
 * case — logout deletes them all, even when nothing is in memory (e.g. after a
 * refresh). Never rejects; a failing check counts as nothing saved, but a stuck
 * one counts as saved — a slow store may still hold one, so logout warns rather
 * than silently deleting it, and still can't hang.
 */
export const hasSavedRecordings = async (): Promise<boolean> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(true), CLEAR_TIMEOUT_MS);
  });
  // True as soon as any store has one, so a stuck store can't hide another's.
  const checked = Promise.any(
    [...savedRecordingChecks].map((check) =>
      Promise.resolve()
        .then(check)
        .then((saved) => saved || Promise.reject(new Error("nothing saved")))
    )
  ).then(
    () => true,
    () => false
  );
  const saved = await Promise.race([checked, timeout]);
  clearTimeout(timer);
  return saved;
};

// Resolves true once deleted. A blocked delete stays pending until other connections close.
const deleteDb = (name: string): Promise<boolean> =>
  new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      resolve(true);
      return;
    }
    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => resolve(true);
      request.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });

/** Deletes interrupted-recording data (PHI) on logout. Never rejects; gives up after 1s so logout can't hang. */
export const clearRecordingRecoveryData = async (): Promise<void> => {
  // Synchronous and ahead of the 1s race below: a delete blocked behind another
  // tab's open connection must not leave the transcript sitting in localStorage.
  // Nothing writes this key any more (MDRS-571) — this evicts snapshots already
  // stored on devices running an earlier build.
  try {
    localStorage.removeItem(LEGACY_TRANSCRIPT_SNAPSHOT_KEY);
  } catch {
    /* localStorage unavailable — best-effort, never block logout. */
  }
  const clear = (async () => {
    // Disown first, or the next chunk/feed write would recreate the DB after the delete.
    await Promise.all([...disowners].map((disown) => disown().catch(() => {})));
    const results = await Promise.all(
      [ASSEMBLYAI_RECOVERY_DB_NAME, SCRIBE_RECOVERY_DB_NAME].map(deleteDb)
    );
    return results.every(Boolean);
  })();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<"timeout">((resolve) => {
    timer = setTimeout(() => resolve("timeout"), CLEAR_TIMEOUT_MS);
  });
  const outcome = await Promise.race([clear, timeout]);
  clearTimeout(timer);
  if (outcome !== true) {
    console.warn(
      "[clearRecordingRecovery] Recovery data may not have been deleted on logout.",
      {
        reason: outcome === "timeout" ? "timeout" : "error",
      }
    );
  }
};
